
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Linkedin, Facebook, Instagram, Mail, Phone, MapPin, Clock, Shield, Users, Award } from 'lucide-react';

function Footer() {
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 0);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // DaChauffeur contact info
  const email = 'info@dachauffeur.ca';
  const mobile = '+1 (234) 567-890';
  const location = 'Toronto, Ontario, Canada';

  return (
    <footer className="bg-gradient-to-br from-primary via-secondary to-black text-white pt-16">
      {/* Footer Top with Background Pattern */}
      <div className="relative">
        {/* Subtle Pattern Overlay */}
        <div className="absolute inset-0 opacity-10">
          <div className="w-full h-full" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M0 40L40 0H20L0 20M40 40V20L20 40'/%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            
            {/* Brand & Contact */}
            <div className="space-y-6">
              {/* Logo */}
              <div className="space-y-4">
                <h3 className="text-3xl font-serif font-bold text-gold-400">
                  DaChauffeur
                </h3>
                <p className="text-gray-300 text-sm leading-relaxed">
                  Premium luxury transportation service providing exceptional chauffeur experiences 
                  for discerning clients across Toronto and beyond.
                </p>
              </div>

              {/* Contact Info */}
              <div className="space-y-4">
                <div className="flex items-center gap-3 text-gray-300 hover:text-gold-400 transition-colors">
                  <Mail size={18} className="text-gold-400" />
                  <a href={`mailto:${email}`} className="hover:text-gold-400 transition-colors">
                    {email}
                  </a>
                </div>
                <div className="flex items-center gap-3 text-gray-300 hover:text-gold-400 transition-colors">
                  <Phone size={18} className="text-gold-400" />
                  <a href={`tel:${mobile}`} className="hover:text-gold-400 transition-colors">
                    {mobile}
                  </a>
                </div>
                <div className="flex items-start gap-3 text-gray-300">
                  <MapPin size={18} className="text-gold-400 mt-1" />
                  <span className="text-sm">{location}</span>
                </div>
              </div>

              {/* Social Media */}
              <div className="space-y-3">
                <p className="text-sm font-semibold text-gold-400">Follow Us</p>
                <div className="flex gap-3">
                  <a 
                    href="https://www.linkedin.com/company/dachauffeur" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="bg-gold-500/20 hover:bg-gold-500 border border-gold-500/30 hover:border-gold-500 rounded-full p-2.5 transition-all duration-300 group"
                  >
                    <Linkedin size={18} className="text-gold-400 group-hover:text-white" />
                  </a>
                  <a 
                    href="https://www.facebook.com/dachauffeur" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="bg-gold-500/20 hover:bg-gold-500 border border-gold-500/30 hover:border-gold-500 rounded-full p-2.5 transition-all duration-300 group"
                  >
                    <Facebook size={18} className="text-gold-400 group-hover:text-white" />
                  </a>
                  <a 
                    href="https://www.instagram.com/dachauffeur" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="bg-gold-500/20 hover:bg-gold-500 border border-gold-500/30 hover:border-gold-500 rounded-full p-2.5 transition-all duration-300 group"
                  >
                    <Instagram size={18} className="text-gold-400 group-hover:text-white" />
                  </a>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div className="space-y-6">
              <h4 className="text-xl font-serif font-bold text-gold-400">Quick Links</h4>
              <ul className="space-y-3">
                <li className="flex items-center gap-2">
                  <ChevronRight size={16} className="text-gold-400" />
                  <Link to="/" className="text-gray-300 hover:text-gold-400 transition-colors">Home</Link>
                </li>
                <li className="flex items-center gap-2">
                  <ChevronRight size={16} className="text-gold-400" />
                  <Link to="/about" className="text-gray-300 hover:text-gold-400 transition-colors">About Us</Link>
                </li>
                <li className="flex items-center gap-2">
                  <ChevronRight size={16} className="text-gold-400" />
                  <Link to="/fleet" className="text-gray-300 hover:text-gold-400 transition-colors">Our Fleet</Link>
                </li>
                <li className="flex items-center gap-2">
                  <ChevronRight size={16} className="text-gold-400" />
                  <Link to="/pricing" className="text-gray-300 hover:text-gold-400 transition-colors">Pricing</Link>
                </li>
                <li className="flex items-center gap-2">
                  <ChevronRight size={16} className="text-gold-400" />
                  <Link to="/booking" className="text-gray-300 hover:text-gold-400 transition-colors">Book Now</Link>
                </li>
                <li className="flex items-center gap-2">
                  <ChevronRight size={16} className="text-gold-400" />
                  <Link to="/contact" className="text-gray-300 hover:text-gold-400 transition-colors">Contact</Link>
                </li>
              </ul>
            </div>

            {/* Services */}
            <div className="space-y-6">
              <h4 className="text-xl font-serif font-bold text-gold-400">Our Services</h4>
              <ul className="space-y-3">
                <li className="flex items-center gap-2">
                  <ChevronRight size={16} className="text-gold-400" />
                  <Link to="/services/airport-transfers" className="text-gray-300 hover:text-gold-400 transition-colors text-sm">Airport Transfers</Link>
                </li>
                <li className="flex items-center gap-2">
                  <ChevronRight size={16} className="text-gold-400" />
                  <Link to="/services/corporate" className="text-gray-300 hover:text-gold-400 transition-colors text-sm">Corporate Travel</Link>
                </li>
                <li className="flex items-center gap-2">
                  <ChevronRight size={16} className="text-gold-400" />
                  <Link to="/services/weddings" className="text-gray-300 hover:text-gold-400 transition-colors text-sm">Weddings & Events</Link>
                </li>
                <li className="flex items-center gap-2">
                  <ChevronRight size={16} className="text-gold-400" />
                  <Link to="/services/hourly" className="text-gray-300 hover:text-gold-400 transition-colors text-sm">Hourly Service</Link>
                </li>
                <li className="flex items-center gap-2">
                  <ChevronRight size={16} className="text-gold-400" />
                  <Link to="/services/point-to-point" className="text-gray-300 hover:text-gold-400 transition-colors text-sm">Point-to-Point</Link>
                </li>
                <li className="flex items-center gap-2">
                  <ChevronRight size={16} className="text-gold-400" />
                  <Link to="/services/special-occasions" className="text-gray-300 hover:text-gold-400 transition-colors text-sm">Special Occasions</Link>
                </li>
              </ul>
            </div>

            {/* Why Choose Us */}
            <div className="space-y-6">
              <h4 className="text-xl font-serif font-bold text-gold-400">Why Choose DaChauffeur</h4>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Clock size={18} className="text-gold-400 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-white text-sm">24/7 Service</p>
                    <p className="text-gray-400 text-xs">Round-the-clock availability</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Shield size={18} className="text-gold-400 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-white text-sm">Professional Drivers</p>
                    <p className="text-gray-400 text-xs">Experienced chauffeurs</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Users size={18} className="text-gold-400 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-white text-sm">Luxury Fleet</p>
                    <p className="text-gray-400 text-xs">Premium vehicles</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Award size={18} className="text-gold-400 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-white text-sm">On-Time Guarantee</p>
                    <p className="text-gray-400 text-xs">Punctual service</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-gold-500 to-gold-600 py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h3 className="text-2xl font-serif font-bold text-primary mb-3">
            Ready to Experience Luxury Transportation?
          </h3>
          <p className="text-primary/80 mb-6">
            Book your premium chauffeur service today and travel in style and comfort.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/booking"
              className="bg-primary text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary/90 transition-colors transform hover:scale-105 transition-transform"
            >
              Book Now
            </Link>
            <Link 
              to="/contact"
              className="bg-transparent text-primary border-2 border-primary px-8 py-3 rounded-lg font-semibold hover:bg-primary hover:text-white transition-all"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </div>

      {/* Footer Bottom */}
      <div className="bg-black/50 border-t border-gold-500/20 py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Copyright */}
            <div className="text-center md:text-left">
              <p className="text-gray-400 text-sm mb-2">
                ©️ 2026 DaChauffeur. All rights reserved.
              </p>
              <p className="text-gray-500 text-xs">
                Premium luxury transportation service in Toronto, Ontario
              </p>
            </div>
            
            {/* Legal Links */}
            <div className="flex flex-col sm:flex-row justify-center md:justify-end items-center gap-4 text-xs">
              <Link to="/terms-of-service" className="text-gray-400 hover:text-gold-400 transition-colors">
                Terms of Service
              </Link>
              <span className="text-gray-600 hidden sm:inline">|</span>
              <Link to="/privacy-policy" className="text-gray-400 hover:text-gold-400 transition-colors">
                Privacy Policy
              </Link>
              <span className="text-gray-600 hidden sm:inline">|</span>
              <Link to="/cancellation-policy" className="text-gray-400 hover:text-gold-400 transition-colors">
                Cancellation Policy
              </Link>
              <span className="text-gray-600 hidden sm:inline">|</span>
              <Link to="/faq" className="text-gray-400 hover:text-gold-400 transition-colors">
                FAQ
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Back to Top Button */}
      <button 
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className={`fixed bottom-8 right-8 bg-gradient-to-r from-gold-500 to-gold-600 text-primary rounded-full p-4 hover:from-gold-600 hover:to-gold-700 transition-all duration-300 shadow-2xl z-40 transform hover:scale-110 ${
          showScrollTop 
            ? 'opacity-100 translate-y-0' 
            : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
        </svg>
      </button>
    </footer>
  );
}

export default Footer;
